import { useState, useEffect, useMemo, useCallback } from 'react';
import { DataGrid, KeenIcon } from '@/components';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';
import { communityValidationSchema } from '@/utils/validations';
import usePagination from '@/hooks/usePagination';
import { ConfirmationDialog } from '@/components/dialogBox';
import {
  CommunityListQueryParams,
  ICommunityData,
  CommunityStatus,
  CommunityListResponse,
} from '@/types/community-management.types';
import {
  useDeleteCommunityMutation,
  useGetCommunitiesQuery,
  useUpdateCommunityMutation,
  useGetCommunityByIdQuery,
  useCreateCommunityMutation,
} from '@/services/CommunityManagementService';
import { CommunityModal } from './CommunityModal';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { usePermissions } from '@/hooks/usePermissions';
import { setSelectedCommunityId } from '@/store/authSlice';
import { Table } from '@tanstack/table-core';
import { useSelector } from 'react-redux';
import { UserTypes } from '@/constant';
import { RootState } from '@/store/store';
import { ErrorApiResponse } from '@/types/api.types';
import { CommunityRole } from '@/types/community-user.types';

const CommunityListings = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    community: ICommunityData | null;
    mode: 'view' | 'edit' | 'create';
  }>({ isOpen: false, community: null, mode: 'view' });
  const [confirmDeleteState, setConfirmDeleteState] = useState<{
    isOpen: boolean;
    communityId: string | null;
  }>({ isOpen: false, communityId: null });

  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [formData, setFormData] = useState<Partial<ICommunityData>>({
    name: '',
    description: '',
    moderators: [],
    moderator_ids: [],
    status: CommunityStatus.GLOBAL,
    tags: [],
    max_users: undefined,
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const { enqueueSnackbar } = useSnackbar();
  const [
    updateCommunity,
    { isLoading: isUpdating, isSuccess: updateSuccess, isError: updateError },
  ] = useUpdateCommunityMutation();
  const [deleteCommunity] = useDeleteCommunityMutation();
  const [communityIdToFetch, setCommunityIdToFetch] = useState<string | null>(
    null
  );
  const userType = useSelector((state: RootState) => state?.auth?.userType);
  const userData = useSelector((state: RootState) => state.auth.userData);
  const { hasPermission } = usePermissions();

  const queryParams: CommunityListQueryParams = {
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: debouncedValue,
    ...(Number(userType) !== UserTypes.ADMIN &&
      userData?.id && { user_id: userData?.id }),
    ...(statusFilter !== 'all' && { status: statusFilter as CommunityStatus }),
  };

  const {
    data: communitiesResponse,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetCommunitiesQuery(queryParams) as {
    data: CommunityListResponse | undefined;
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
    refetch: () => void;
  };

  useEffect(() => {
    if (typeof refetch === 'function') {
      refetch();
    }
  }, [debouncedValue, statusFilter, refetch]);

  const {
    data: communityResponse,
    isSuccess: communityFetchSuccess,
    error: communityFetchError,
    isFetching: communityFetching,
  } = useGetCommunityByIdQuery(communityIdToFetch!, {
    skip: !communityIdToFetch,
  });

  useEffect(() => {
    if (communitiesResponse) {
      handleTotalPages(communitiesResponse.total_pages || 1);
      handleTotalItems(communitiesResponse.total_items || 0);
    }
  }, [communitiesResponse, handleTotalPages, handleTotalItems]);

  useEffect(() => {
    if (communityFetchSuccess && communityResponse) {
      const communityData: ICommunityData = {
        id: communityResponse.id,
        name: communityResponse.name || '',
        description: communityResponse.description || '',
        moderator_id: communityResponse.moderator_id || '',
        status:
          CommunityStatus[
            communityResponse.status as keyof typeof CommunityStatus
          ] || CommunityStatus.GLOBAL,
        tags: communityResponse.tags || [],
        max_users: communityResponse.max_users,
        language: communityResponse.language || 'en',
        created_at: communityResponse.created_at || '',
        updated_at: communityResponse.updated_at || '',
        moderator: {
          id: communityResponse.moderator?.id || '',
          name:
            communityResponse.moderator?.name ||
            communityResponse.moderator?.email ||
            'Unknown User',
          email: communityResponse.moderator?.email || '',
        },
        moderators: communityResponse.moderators || [],
        moderator_ids: communityResponse.moderator_ids || [],
        user_role: communityResponse.user_role || '',
        translations: communityResponse.translations || {},
      };
      setFormData(communityData);
      setErrors({});
      setModalState((prev) => ({
        ...prev,
        isOpen: true,
        community: communityData,
      }));
      setCommunityIdToFetch(null);
    }
  }, [communityFetchSuccess, communityResponse]);

  useEffect(() => {
    if (communityFetchError) {
      enqueueSnackbar('Failed to fetch community details', {
        variant: 'error',
      });
      setCommunityIdToFetch(null);
    }
  }, [communityFetchError, enqueueSnackbar]);

  useEffect(() => {
    if (updateSuccess) {
      enqueueSnackbar('Community updated successfully', { variant: 'success' });
      handleCloseModal();
      refetch();
    }
  }, [updateSuccess, enqueueSnackbar, refetch]);

  useEffect(() => {
    if (updateError) {
      enqueueSnackbar('Failed to update community', { variant: 'error' });
    }
  }, [updateError, enqueueSnackbar]);

  // Updated filteredData to combine joined and other arrays
  const filteredData: ICommunityData[] = useMemo(() => {
    if (!communitiesResponse) return [];

    const combined = [
      ...(communitiesResponse.other || []),
      ...(communitiesResponse.joined || []),
    ];

    return combined.map(
      (community: ICommunityData): ICommunityData => ({
        id: community.id,
        name: community.name,
        description: community.description,
        moderator_id: community.moderator_id,
        status: community.status,
        tags: community.tags,
        max_users: community.max_users,
        created_at: community.created_at,
        updated_at: community.updated_at,
        moderator: {
          id: community.moderator?.id || '',
          name:
            community.moderator?.name ||
            community.moderator?.email ||
            'Unknown User',
          email: community.moderator?.email,
        },
        moderators: community.moderators,
        user_role: community.user_role,
      })
    );
  }, [communitiesResponse]);

  const resetFilters = () => {
    if (searchTerm || statusFilter !== 'all') resetSearch();
    setStatusFilter('all');
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const handleOpenModal = useCallback(
    (community: ICommunityData | null, mode: 'view' | 'edit' | 'create') => {
      if (mode === 'create') {
        setModalState({ isOpen: true, community: null, mode: 'create' });
        setFormData({
          name: '',
          description: '',
          moderators: [],
          moderator_ids: [],
          status: CommunityStatus.GLOBAL,
          tags: [],
          max_users: undefined,
        });
        setErrors({});
      } else if (community?.id) {
        const moderators = community.moderators || [];
        setFormData({
          id: community.id,
          name: community.name,
          description: community.description,
          moderators: moderators,
          moderator_ids: moderators.map((m) => m.id),
          status: community.status,
          tags: community.tags,
          max_users: community.max_users,
          // Keeping for backward compatibility
          moderator: moderators[0],
          moderator_id: moderators[0]?.id || '',
        });
        setErrors({});
        setModalState({ isOpen: true, community, mode });
        setCommunityIdToFetch(community.id);
      }
    },
    []
  );

  const handleCloseModal = () => {
    setModalState({ isOpen: false, community: null, mode: 'view' });
    setFormData({});
    setErrors({});
    setCommunityIdToFetch(null);
  };

  const [createCommunity] = useCreateCommunityMutation();

  const handleSubmit = async (data: Partial<ICommunityData>) => {
    try {
      if (modalState.mode === 'create') {
        await communityValidationSchema.validate(data, {
          abortEarly: false,
        });

        const payload = {
          ...data,
        };

        await createCommunity(payload).unwrap();
        enqueueSnackbar('Community created successfully', {
          variant: 'success',
        });
        handleCloseModal();
      }
      if (modalState.mode === 'edit' && modalState.community) {
        const originalData = modalState.community;
        const payload: Partial<ICommunityData> = {};

        if (
          JSON.stringify(data.moderator_ids) !==
          JSON.stringify(originalData.moderator_ids)
        ) {
          payload.moderator_ids = data.moderator_ids || [];
          payload.moderators = data.moderators || [];
        }
        if (data.name !== originalData.name) {
          payload.name = data.name || '';
        }
        if (data.description !== originalData.description) {
          payload.description = data.description || '';
        }
        if (data.status !== originalData.status) {
          payload.status = data.status || CommunityStatus.GLOBAL;
        }
        if (JSON.stringify(data.tags) !== JSON.stringify(originalData.tags)) {
          payload.tags = data.tags;
        }
        // Special handling for max_users to ensure empty values are properly sent
        if (
          data.max_users !== originalData.max_users ||
          (data.max_users === undefined && originalData.max_users !== undefined)
        ) {
          payload.max_users =
            data.max_users === undefined ? null : data.max_users;
        }
        if (!payload.tags) {
          payload.tags = communityResponse?.tags;
        }

        // Always include translations from the modal data when present,
        // so the backend receives the latest translations array on update.
        if (data.translations) {
          payload.translations = data.translations;
        }

        const hasChanges = Object.keys(payload).length > 0;

        if (hasChanges) {
          await updateCommunity({
            community_id: modalState.community.id as string,
            data: payload,
          }).unwrap();
          handleCloseModal();
        } else {
          enqueueSnackbar({ message: 'No changes to update', variant: 'info' });
          handleCloseModal();
        }
      }
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors: { [key: string]: string } = {};
        err.inner.forEach((error) => {
          if (error.path) validationErrors[error.path] = error.message;
        });
        setErrors(validationErrors);
        enqueueSnackbar({
          message: `Please fix the form errors (${err.inner.length} errors occurred)`,
          variant: 'error',
        });
      } else {
        const errorMessage =
          (err as ErrorApiResponse).data?.message || 'An error occurred';
        enqueueSnackbar({
          message: errorMessage,
          variant: 'error',
        });
      }
    }
  };

  const handleCommunityDetails = useCallback(
    (communityId: string) => {
      navigate(`/community/details`);
      dispatch(setSelectedCommunityId({ selectedCommunityId: communityId }));
    },
    [navigate, dispatch]
  );

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: CommunityStatus.PRIVATE, label: 'Private' },
    { value: CommunityStatus.GLOBAL, label: 'Global' },
    { value: CommunityStatus.CLOSED, label: 'Closed' },
    { value: CommunityStatus.DELETED, label: 'Deleted' },
  ];

  const columns = useMemo(
    () => [
      {
        accessor: (row: ICommunityData) => row.name,
        id: 'name',
        header: 'Community Name',
        enableSorting: false,
        cell: ({ row }: { row: { original: ICommunityData } }) => (
          <button
            type="button"
            onClick={() => handleCommunityDetails(row.original.id as string)}
            className="text-sm font-medium text-gray-900 hover:text-primary-active focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-1"
            aria-label={`View ${row.original.name} details`}
          >
            {row.original.name}
          </button>
        ),
        meta: { className: 'min-w-[200px]' },
      },
      {
        accessor: (row: ICommunityData) =>
          row.moderators?.map((m) => m.name).join(', ') || row.moderator?.name,
        id: 'moderators',
        header: 'Community Admin or Moderator',
        enableSorting: false,
        cell: ({ row }: { row: { original: ICommunityData } }) => {
          const fallbackModerator = row.original.moderator;

          return (
            <ul
              className="flex flex-wrap gap-1"
              aria-label="Community moderators"
            >
              {row.original.moderators?.length ? (
                row.original.moderators.map((moderator, index) => (
                  <li
                    key={moderator.id || index}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200"
                  >
                    {moderator.name !== null ? moderator.name : moderator.email}
                  </li>
                ))
              ) : fallbackModerator ? (
                <li className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 contrast:bg-blue-900 text-blue-800 dark:text-blue-200 contrast:text-blue-200">
                  {fallbackModerator.name || fallbackModerator.email || '-'}
                </li>
              ) : (
                <span
                  className="text-gray-500 contrast:text-white/50"
                  role="listitem"
                >
                  -
                </span>
              )}
            </ul>
          );
        },
        meta: { className: 'min-w-[250px]' },
      },
      {
        accessor: (row: ICommunityData) => row.tags,
        id: 'tags',
        header: 'Tags',
        enableSorting: false,
        cell: ({ row }: { row: { original: ICommunityData } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.tags?.join(', ') || '-'}
          </span>
        ),
        meta: { className: 'min-w-[200px]' },
      },
      {
        accessor: (row: ICommunityData) => row.max_users,
        id: 'max_users',
        header: 'Max Users',
        enableSorting: false,
        cell: ({ row }: { row: { original: ICommunityData } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.max_users ?? '-'}
          </span>
        ),
        meta: { className: 'min-w-[150px]' },
      },
      {
        accessor: (row: ICommunityData) => row.status,
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }: { row: { original: ICommunityData } }) => (
          <span
            className={`ml-2 text-sm font-medium ${
              row.original.status === CommunityStatus.PRIVATE
                ? 'text-yellow-700 dark:text-yellow-500 contrast:text-yellow-500'
                : row.original.status === CommunityStatus.GLOBAL
                  ? 'text-green-700 dark:text-green-500 contrast:text-green-500'
                  : 'text-red-500'
            }`}
            role="status"
            aria-label={`Status: ${row.original.status}`}
          >
            {row.original.status.split('_').join(' ')}
          </span>
        ),
        meta: { className: 'min-w-[150px]' },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({
          row,
        }: {
          row: { original: ICommunityData; index: number };
          table: Table<ICommunityData>;
        }) => (
          <div
            className="flex items-center gap-2"
            role="group"
            aria-label={`Actions for ${row.original.name}`}
          >
            <button
              type="button"
              onClick={() => handleCommunityDetails(row.original.id as string)}
              className="btn btn-sm btn-icon btn-light btn-outline"
              aria-label={`View ${row.original.name} details`}
              title="View community details"
            >
              <KeenIcon
                type="filled"
                icon="eye"
                className="text-gray-500"
                aria-hidden="true"
              />
            </button>
            {hasPermission('community_management_update') ||
            row.original.user_role === CommunityRole.COMMUNITY_ADMIN ? (
              <button
                type="button"
                onClick={() => {
                  handleOpenModal(row.original, 'edit');
                }}
                className="btn btn-sm btn-icon btn-light btn-outline"
                aria-label={`Edit ${row.original.name}`}
                title="Edit community"
                data-testid="edit-button"
              >
                <KeenIcon
                  type="filled"
                  icon="pencil"
                  className="text-gray-500"
                  aria-hidden="true"
                />
              </button>
            ) : null}

            {hasPermission('community_management_delete') ? (
              <button
                type="button"
                onClick={() => {
                  setConfirmDeleteState({
                    isOpen: true,
                    communityId: row.original.id as string,
                  });
                }}
                className="btn btn-sm btn-icon btn-light btn-outline"
                aria-label={`Delete ${row.original.name}`}
                title="Delete community"
              >
                <KeenIcon
                  type="filled"
                  icon="trash"
                  className="text-gray-500"
                  aria-hidden="true"
                />
              </button>
            ) : null}
          </div>
        ),
        meta: { className: 'w-[80px]' },
      },
    ],
    [handleOpenModal, handleCommunityDetails, hasPermission]
  );

  async function handleDelete(): Promise<void> {
    if (!confirmDeleteState.communityId) return;
    try {
      await deleteCommunity(confirmDeleteState.communityId).unwrap();
      enqueueSnackbar('Community deleted successfully', { variant: 'success' });
      setConfirmDeleteState({ isOpen: false, communityId: null });
      refetch();
    } catch {
      enqueueSnackbar('Failed to delete community', { variant: 'error' });
    }
  }

  return (
    <div className="community-listings card card-grid w-full">
      <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
        <div className="flex flex-wrap items-center gap-2 sm:gap-4 w-full">
          <div className="flex items-center gap-2" role="search">
            <div className="flex-shrink-0">
              <label
                className="input input-sm h-8 sm:h-10 flex items-center"
                htmlFor="search-community"
                aria-label="Search communities"
              >
                <KeenIcon
                  type="filled"
                  icon="magnifier"
                  className="text-gray-500"
                  aria-hidden="true"
                />
                <input
                  id="search-community"
                  placeholder="Search communities"
                  type="text"
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                  aria-label="Search communities by name"
                  role="searchbox"
                />
              </label>
            </div>
            <div className="flex-shrink-0 w-full sm:w-64">
              <Select
                id="status-filter"
                options={statusOptions}
                value={statusOptions.find(
                  (option) => option.value === statusFilter
                )}
                onChange={(selectedOption) =>
                  setStatusFilter(selectedOption ? selectedOption.value : 'all')
                }
                isDisabled={isLoading || isFetching || isUpdating}
                placeholder="Select status"
                className="react-select-container"
                classNamePrefix="react-select"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: '0.5rem',
                    borderColor:
                      document.documentElement.classList.contains('contrast') ||
                      document.documentElement.classList.contains('dark')
                        ? '#363843'
                        : '#d1d5db',
                    minHeight: '2.5rem',
                    fontSize: '0.875rem',
                    lineHeight: '1.25rem',
                    boxShadow: 'none',
                    '&:hover': {
                      borderColor:
                        document.documentElement.classList.contains(
                          'contrast'
                        ) || document.documentElement.classList.contains('dark')
                          ? '#636674'
                          : '#93c5fd',
                    },
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 50,
                    marginTop: '0.5rem',
                    borderRadius: '0.375rem',
                    backgroundColor:
                      document.documentElement.classList.contains('contrast') ||
                      document.documentElement.classList.contains('dark')
                        ? '#0b0c10'
                        : 'white',
                    boxShadow:
                      document.documentElement.classList.contains('contrast') ||
                      document.documentElement.classList.contains('dark')
                        ? '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                        : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  }),
                  option: (base, state) => ({
                    ...base,
                    fontSize: '0.875rem',
                    backgroundColor:
                      document.documentElement.classList.contains('contrast') ||
                      document.documentElement.classList.contains('dark')
                        ? '#0b0c10'
                        : state.isSelected
                          ? '#e0f2fe'
                          : state.isFocused
                            ? '#f3f4f6'
                            : 'white',
                    color:
                      document.documentElement.classList.contains('contrast') ||
                      document.documentElement.classList.contains('dark')
                        ? state.isSelected
                          ? '#ffffff'
                          : '#b5b7c8'
                        : '#1f2937',
                    padding: '0.5rem 0.75rem',
                    cursor: 'pointer',
                  }),
                  placeholder: (base) => ({
                    ...base,
                    color:
                      document.documentElement.classList.contains('contrast') ||
                      document.documentElement.classList.contains('dark')
                        ? '#636674'
                        : '#9ca3af',
                  }),
                }}
                aria-label="Filter communities by status"
              />
            </div>
          </div>
        </div>
        <div
          className="flex items-center gap-2"
          role="toolbar"
          aria-label="Filter controls"
        >
          {(searchTerm || statusFilter !== 'all') && (
            <button
              type="button"
              data-testid="reset-filters"
              className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={resetFilters}
              aria-label="Clear all filters"
              title="Clear all filters"
            >
              <KeenIcon
                type="solid"
                icon="arrows-circle"
                className="text-gray-600"
                aria-hidden="true"
              />
              Clear Filters
            </button>
          )}
        </div>
      </div>
      <div className="card-body">
        {isLoading || isFetching || communityFetching ? (
          <div
            className="text-center py-4"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <span className="sr-only">Loading communities data</span>
            Loading communities...
          </div>
        ) : isError ? (
          <div
            className="text-center py-4 text-red-500"
            role="alert"
            aria-live="assertive"
          >
            Failed to load communities. Please try again.
          </div>
        ) : filteredData.length === 0 ? (
          <div
            className="text-center py-4 text-gray-500"
            role="status"
            aria-live="polite"
          >
            No communities are currently assigned. Please check back later for
            available listings.
          </div>
        ) : (
          <div className="community-grid-wrapper">
            <DataGrid
              columns={columns}
              data={filteredData}
              rowSelect={false}
              loading={isLoading || isFetching || communityFetching}
              pagination={{
                page: pagination.pageIndex,
                size: pagination.pageSize,
                more: true,
                totalItems,
                totalPages,
                setPagination,
                info: `{from} - {to} of {count}`,
                sizes: [5, 10, 25, 50, 100],
                sizesLabel: 'Show',
                sizesDescription: 'per page',
              }}
              sorting={[{ id: 'name', desc: false }]}
              serverSide={true}
              onFilterReset={resetFilters}
              hasActiveFilters={!!searchTerm || statusFilter !== 'all'}
              aria-label="Community listings table"
            />
          </div>
        )}
        <CommunityModal
          mode={modalState.mode}
          formData={formData}
          setFormData={setFormData}
          errors={errors}
          setErrors={setErrors}
          onSubmit={handleSubmit}
          isSubmitting={isUpdating}
          open={modalState.isOpen}
          onClose={handleCloseModal}
        />
        <ConfirmationDialog
          data-testid="delete-confirmation-dialog"
          open={confirmDeleteState.isOpen}
          onClose={() =>
            setConfirmDeleteState({ isOpen: false, communityId: null })
          }
          onConfirm={handleDelete}
          message="Are you sure you want to delete this community?"
          cancelButtonText="No"
          confirmButtonText="Yes"
          aria-labelledby="delete-confirmation-title"
          aria-describedby="delete-confirmation-description"
        />
      </div>
    </div>
  );
};

export { CommunityListings };
